import React, { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiLifeBuoy, FiSend } from 'react-icons/fi';
import api from '../config/api';
import { queryKeys } from '../config/queryKeys';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn } from '../components/motion';
import { appAlert } from '../utils/appDialog';
import './Support.css';

const EMPTY_FORM = {
  requester_email: '',
  requester_name: '',
  subject: '',
  message: '',
  priority: 'normal',
  assignee_id: '',
  tags: '',
};

function validateForm(form) {
  const errors = {};
  const email = form.requester_email.trim();
  if (!email) errors.requester_email = 'Customer email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.requester_email = 'Enter a valid email address';
  }
  if (!form.subject.trim()) errors.subject = 'Subject is required';
  if (!form.message.trim()) errors.message = 'Message is required';
  return errors;
}

const SupportNewTicket = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('support_manage');
  const canCreate = hasPermission('support_create') || canManage;

  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    requester_email: searchParams.get('email') || '',
  }));
  const [touched, setTouched] = useState({});

  const errors = useMemo(() => validateForm(form), [form]);
  const showError = (field) => Boolean(touched[field] && errors[field]);

  const { data: agentsData } = useQuery(
    queryKeys.supportAgents,
    async () => {
      const { data } = await api.get('/support/agents');
      return data.agents || [];
    },
    { enabled: canManage }
  );

  const createMutation = useMutation(
    async () => {
      const validation = validateForm(form);
      if (Object.keys(validation).length) {
        setTouched({
          requester_email: true,
          subject: true,
          message: true,
        });
        throw new Error(Object.values(validation)[0]);
      }

      const tags = form.tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      const { data } = await api.post('/support/tickets', {
        requester_email: form.requester_email.trim(),
        requester_name: form.requester_name.trim() || undefined,
        subject: form.subject.trim(),
        body_text: form.message.trim(),
        priority: form.priority,
        assignee_id: form.assignee_id ? Number(form.assignee_id) : undefined,
        tags: tags.length ? tags : undefined,
      });
      return data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['support']);
        const id = data?.ticket?.id;
        if (id) navigate(`/support/${id}`, { replace: true });
        else navigate('/support', { replace: true });
      },
      onError: (err) => {
        appAlert(err.response?.data?.error || err.message || 'Failed to create ticket');
      },
    }
  );

  if (!canCreate) {
    return <Navigate to="/support" replace />;
  }

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    createMutation.mutate();
  };

  return (
    <div className="support-page support-new-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <p className="support-new-kicker">
            <Link to="/support" className="support-back-link">
              <FiArrowLeft aria-hidden /> Back to queue
            </Link>
          </p>
          <h1>
            <FiLifeBuoy aria-hidden /> Log new ticket
          </h1>
          <p className="page-subtitle">
            Create a support ticket with the customer details. Required fields are marked.
            Public replies later will email the customer from support@gobunnyy.com.
          </p>
        </div>
      </FadeIn>

      <form className="support-new-form" onSubmit={onSubmit} noValidate>
        <section className="support-new-card" aria-labelledby="support-new-requester">
          <h2 id="support-new-requester">Requester</h2>
          <div className="support-new-grid">
            <div className={`form-group${showError('requester_email') ? ' has-error' : ''}`}>
              <label htmlFor="support-requester-email">
                Customer email <span className="support-required">*</span>
              </label>
              <input
                id="support-requester-email"
                type="email"
                autoComplete="email"
                value={form.requester_email}
                onChange={(e) => setField('requester_email', e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, requester_email: true }))}
                placeholder="customer@company.com"
                required
                aria-invalid={showError('requester_email')}
                aria-describedby={
                  showError('requester_email') ? 'support-requester-email-error' : undefined
                }
              />
              {showError('requester_email') && (
                <p id="support-requester-email-error" className="support-field-error" role="alert">
                  {errors.requester_email}
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="support-requester-name">Customer name</label>
              <input
                id="support-requester-name"
                type="text"
                value={form.requester_name}
                onChange={(e) => setField('requester_name', e.target.value)}
                placeholder="Optional display name"
              />
            </div>
          </div>
        </section>

        <section className="support-new-card" aria-labelledby="support-new-details">
          <h2 id="support-new-details">Ticket details</h2>
          <div className={`form-group${showError('subject') ? ' has-error' : ''}`}>
            <label htmlFor="support-subject">
              Subject <span className="support-required">*</span>
            </label>
            <input
              id="support-subject"
              type="text"
              value={form.subject}
              onChange={(e) => setField('subject', e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, subject: true }))}
              placeholder="Short summary of the issue"
              required
              maxLength={500}
              aria-invalid={showError('subject')}
            />
            {showError('subject') && (
              <p className="support-field-error" role="alert">
                {errors.subject}
              </p>
            )}
          </div>

          <div className={`form-group${showError('message') ? ' has-error' : ''}`}>
            <label htmlFor="support-message">
              Message <span className="support-required">*</span>
            </label>
            <textarea
              id="support-message"
              rows={8}
              value={form.message}
              onChange={(e) => setField('message', e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, message: true }))}
              placeholder="Paste or write the customer’s first message…"
              required
              aria-invalid={showError('message')}
            />
            {showError('message') && (
              <p className="support-field-error" role="alert">
                {errors.message}
              </p>
            )}
          </div>

          <div className="support-new-grid support-new-grid--3">
            <div className="form-group">
              <label htmlFor="support-priority">Priority</label>
              <select
                id="support-priority"
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {canManage ? (
              <div className="form-group">
                <label htmlFor="support-assignee">Assignee</label>
                <select
                  id="support-assignee"
                  value={form.assignee_id}
                  onChange={(e) => setField('assignee_id', e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {(agentsData || []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {[a.first_name, a.last_name].filter(Boolean).join(' ') || a.email}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="support-tags">Tags</label>
              <input
                id="support-tags"
                type="text"
                value={form.tags}
                onChange={(e) => setField('tags', e.target.value)}
                placeholder="billing, onboarding…"
              />
              <p className="support-field-hint">Comma-separated</p>
            </div>
          </div>
        </section>

        <div className="support-new-actions">
          <Link to="/support" className="btn btn-secondary">
            Cancel
          </Link>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={createMutation.isLoading}
          >
            <FiSend aria-hidden />{' '}
            {createMutation.isLoading ? 'Creating…' : 'Create ticket'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupportNewTicket;
